import { Auth0Provider } from '@auth0/auth0-react'
import { ThemeProvider } from '@emotion/react'
import { CssBaseline } from '@mui/material'
import React, { Suspense } from 'react'
import { Provider as ReduxStoreProvider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import type { ToastPosition } from 'react-toastify'
import { ToastContainer } from 'react-toastify'
import { IntercomProvider } from 'react-use-intercom'
import { PersistGate } from 'redux-persist/integration/react'

import AppRoutes from '@/AppRoutes'
import LoadingSpinner from '@/components/LoadingSpinner'
import '@/index.css'
import { persistor, store } from '@/store'
import theme from '@/theme'
import auth0Config from '@/utils/auth0'
import { validateEnvVariables } from '@/utils/envValidation'

import AxiosInterceptor from './AxiosInterceptor'
import './sentry'

// Validate environment variables at startup
validateEnvVariables()

const intercomAppId = import.meta.env.VITE_INTERCOM_APPID

interface ToastConfig {
  position: ToastPosition
  autoClose: number
  hideProgressBar: boolean
  newestOnTop: boolean
  closeOnClick: boolean
  rtl: boolean
  draggable: boolean
  pauseOnHover: boolean
}

const toastConfig: ToastConfig = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  draggable: true,
  pauseOnHover: false,
}

const App: React.FC = () => {
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
      }}
    >
      <BrowserRouter>
        <ReduxStoreProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <IntercomProvider appId={intercomAppId ?? ''} autoBoot>
                <ToastContainer {...toastConfig} />
                <AxiosInterceptor>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AppRoutes />
                  </Suspense>
                </AxiosInterceptor>
              </IntercomProvider>
            </ThemeProvider>
          </PersistGate>
        </ReduxStoreProvider>
      </BrowserRouter>
    </Auth0Provider>
  )
}

export default App
